const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// Set by the backend when it silently extends an about-to-expire token for a user who is
// actively making requests (see JwtAuthenticationFilter).
const REFRESHED_TOKEN_HEADER = 'X-Refreshed-Token';

// Dispatched when a request fails with 401 (no/expired/invalid token) so the app can react
// (e.g. AuthContext clears its state and route guards redirect to /login) even though apiClient
// itself has no access to React context.
export const SESSION_EXPIRED_EVENT = 'shellgame:session-expired';

export function getStoredToken(): string | null {
  return localStorage.getItem('shellgame_token');
}

export function setStoredToken(token: string): void {
  localStorage.setItem('shellgame_token', token);
}

export function clearStoredToken(): void {
  localStorage.removeItem('shellgame_token');
}

export interface ApiErrorBody {
  message?: string;
  details?: string[];
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, fallbackMessage: string) {
    super(body?.message ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function adoptRenewedToken(response: Response): void {
  // The user is actively using the app: transparently adopt the renewed token so their session
  // keeps going instead of being cut off at the original token's expiration.
  const renewedToken = response.headers?.get?.(REFRESHED_TOKEN_HEADER);
  if (renewedToken) {
    setStoredToken(renewedToken);
  }
}

async function handleErrorResponse(path: string, response: Response): Promise<never> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Non-JSON or empty error body; fall back to the generic message below.
  }
  if (response.status === 401) {
    // No valid/renewed token: the session is over (expired from inactivity, or never existed).
    clearStoredToken();
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
  throw new ApiError(response.status, body, `Request failed for ${path}: ${response.status}`);
}

export async function apiClient<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  adoptRenewedToken(response);

  if (!response.ok) {
    await handleErrorResponse(path, response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    return undefined as T;
  }
}
